import { getFileItemsRefresh } from 'owa-groups-services';
import { isSuccess } from 'owa-groups-services';
import BaseMailboxFileItem from 'owa-service/lib/contract/BaseMailboxFileItem';
import GetFilesResponseType from 'owa-service/lib/contract/GetFilesResponseType';
import updateRecentFilesStore from 'owa-group-files-hub-store/lib/actions/updateRecentFilesStore';
import updateRecentFilesLoadingAndErrorStatus from 'owa-group-files-hub-store/lib/actions/updateRecentFilesLoadingStatus';
import { getRecentFilesList } from 'owa-group-files-hub-store/lib/selectors/getRecentFilesList';
import { getFileItemsPromiseOverride } from 'owa-groups-adaptors';
import {
    FileInformationConverterForGetFileItems,
    MaxFiles,
} from '../utils/FileInformationConverterForGetFileItems';
import GetFileItemsCacheStatus from 'owa-service/lib/contract/GetFileItemsCacheStatus';
import FileListLoadingStatus from 'owa-group-files-hub-store/lib/Schema/FileListLoadingStatus';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import prefetchFilePreviewInformationAction from '../actions/PrefetchFilePreviewInformationAction';
import {
    logResponseDataForOwaServerCalls,
    isServiceFetchError,
    logServerCallErrorMessage,
    logErrorForEmptyOrNullResponse,
} from 'owa-server-call-logging/lib/owaServerCallLogging';
import { BaseFileInformation } from 'owa-group-files-hub-store';
import { logFileListInformation } from '../logging/logFileListInformation';
import { getErrorFromObject } from '../logging/genericInformationLogging';
import { getGroupMailboxExternalId } from 'owa-groups-shared-store';
import GetFileItemsResponseFailureType from 'owa-service/lib/contract/GetFileItemsResponseFailureType';

function parseGetFileItemsRefreshResponse(
    response: any,
    datapoint: PerformanceDatapoint,
    groupSmtpAddress: string
) {
    if (datapoint) {
        datapoint.addCheckmark('ParsingResponse');
    }

    const recentFilesList = getRecentFilesList(groupSmtpAddress);

    if (response && response.Body) {
        if (isSuccess(response.Body)) {
            const fileList: BaseMailboxFileItem[] = response.Body.FileItems;
            const responseStatus: GetFilesResponseType = response.Body.ResponseStatusType;
            const failureReason: GetFileItemsResponseFailureType = response.Body.FailureReason;
            const cacheStatus: GetFileItemsCacheStatus = response.Body.CacheStatus;

            if (datapoint) {
                datapoint.addCustomData({
                    ResponseStatus: responseStatus.toString(),
                });
            }

            if (failureReason !== GetFileItemsResponseFailureType.None) {
                recentFilesList.fileListRefreshErrorStatus = failureReason.toString();
            }

            // success - refresh the list
            if (
                responseStatus == GetFilesResponseType.Success &&
                cacheStatus != null &&
                cacheStatus.IsRetrievedFromCache == false
            ) {
                recentFilesList.fileItems = createFileInformationListAndStartPreviewPrefetch(
                    fileList,
                    groupSmtpAddress,
                    datapoint
                );

                recentFilesList.fileListRefreshLoadingStatus = FileListLoadingStatus.Success;
            }

            // partial success => if the list is still loading or cache call failed. i.e we dont have any response from cache, we populate the list else we ignore it.
            if (responseStatus == GetFilesResponseType.PartialSuccess) {
                if (
                    recentFilesList.fileListCacheLoadingStatus == FileListLoadingStatus.Failed ||
                    recentFilesList.fileListCacheLoadingStatus == FileListLoadingStatus.Loading
                ) {
                    recentFilesList.fileItems = createFileInformationListAndStartPreviewPrefetch(
                        fileList,
                        groupSmtpAddress,
                        datapoint
                    );
                }

                recentFilesList.fileListRefreshLoadingStatus = FileListLoadingStatus.PartialSuccess;
            }

            // failure
            if (responseStatus == GetFilesResponseType.Failure) {
                recentFilesList.fileListRefreshLoadingStatus = FileListLoadingStatus.Failed;
            }
        } else {
            if (datapoint) {
                logServerCallErrorMessage(datapoint, 'Server response is complete failure');
                datapoint.endWithError(
                    DatapointStatus.ServerError,
                    new Error('Server response is complete failure')
                );
            }

            recentFilesList.fileListRefreshErrorStatus = 'Server response is complete failure';
            recentFilesList.fileListRefreshLoadingStatus = FileListLoadingStatus.Failed;
        }

        // Add to (if undefined), or update, the recent files list map
        updateRecentFilesStore(groupSmtpAddress, recentFilesList);
    } else {
        if (datapoint) {
            logErrorForEmptyOrNullResponse(datapoint, response);
            datapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(
                    'Error response from server encountered while trying to read the response'
                )
            );
        }

        recentFilesList.fileListRefreshLoadingStatus = FileListLoadingStatus.Failed;
        recentFilesList.fileListRefreshErrorStatus =
            'Error response from server encountered while trying to read the response';
        updateRecentFilesStore(groupSmtpAddress, recentFilesList);
    }
}

function createFileInformationListAndStartPreviewPrefetch(
    fileItems: BaseMailboxFileItem[],
    groupSmtpAddress: string,
    datapoint: PerformanceDatapoint
): BaseFileInformation[] {
    const fileList = new FileInformationConverterForGetFileItems(
        fileItems,
        groupSmtpAddress,
        new Date() // For refresh call, use current time as the file retrieved time.
    ).getFileInformation();

    logFileListInformation(fileList, datapoint);

    // prefetch file preview information
    prefetchFilePreviewInformationAction(fileList);

    return fileList;
}

export function refreshRecentFiles(groupSmtpAddress: string) {
    const datapoint = new PerformanceDatapoint('GroupFilesHubGetFileItemsRefreshActionDP');

    datapoint.addCustomProperty('groupExternalId', getGroupMailboxExternalId(groupSmtpAddress));

    if (getFileItemsPromiseOverride) {
        getFileItemsPromiseOverride(groupSmtpAddress)
            .then((response: any) => {
                parseGetFileItemsRefreshResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                updateRecentFilesLoadingAndErrorStatus(
                    groupSmtpAddress,
                    FileListLoadingStatus.Failed,
                    error.stack,
                    false
                );

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    } else {
        getFileItemsRefresh(groupSmtpAddress, MaxFiles)
            .then((response: any) => {
                parseGetFileItemsRefreshResponse(response, datapoint, groupSmtpAddress);
                datapoint.end();
            })
            .catch(error => {
                if (isServiceFetchError(error)) {
                    logResponseDataForOwaServerCalls(datapoint, error.response);
                }

                updateRecentFilesLoadingAndErrorStatus(
                    groupSmtpAddress,
                    FileListLoadingStatus.Failed,
                    error.stack,
                    false
                );

                datapoint.endWithError(DatapointStatus.ServerError, getErrorFromObject(error));
            });
    }
}
